import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, AlertColor, Button, Snackbar } from '@mui/material';

interface ToastAction {
  label: string;
  /** In-app path for "View step" etc. (e.g. /app/plans/:planId/tasks/:stepId) */
  to: string;
}

interface ToastOptions {
  message: string;
  severity?: AlertColor;
  autoHideDuration?: number;
  /** Optional action button (e.g. "View step" deep link) */
  action?: ToastAction;
}

interface ToastContextType {
  showToast: (options: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastState extends Required<Omit<ToastOptions, 'action'>> {
  open: boolean;
  action?: ToastAction;
}

const DEFAULT_AUTO_HIDE_DURATION = 5000;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: '',
    severity: 'info',
    autoHideDuration: DEFAULT_AUTO_HIDE_DURATION,
  });

  const showToast = useCallback((options: ToastOptions) => {
    setToast({
      open: true,
      message: options.message,
      severity: options.severity ?? 'info',
      autoHideDuration: options.autoHideDuration ?? DEFAULT_AUTO_HIDE_DURATION,
      action: options.action,
    });
  }, []);

  const handleClose = useCallback(() => {
    setToast((prev) => ({ ...prev, open: false }));
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar
        open={toast.open}
        autoHideDuration={toast.autoHideDuration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 1 }}
      >
        <Alert
          onClose={handleClose}
          severity={toast.severity}
          sx={{ width: '100%' }}
          action={
            toast.action ? (
              <Button
                component={Link}
                to={toast.action.to}
                color="inherit"
                size="small"
                onClick={handleClose}
              >
                {toast.action.label}
              </Button>
            ) : undefined
          }
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
